import React, { useMemo } from "react";
import DumbBellChart from "./DumbBellChart";
import styled from "styled-components";
import { PERIOD_DASHBOARD_TO_HOURS, LOAD_INTERVAL } from "./utils";
import useDashboardData from "./useDashboardData";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";
import { parseResult, MAX_SERVICES_TO_FETCH, MAX_SERVICES_TO_SHOW } from "views/Status/TCPOComparison/api";

const Wrapper = styled.div`
  width: 100%;
`;
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;
  position: relative;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  flex: 1;

  .tcpo {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
    margin: 0 3px;
    &.with {
      background-color: #dc143c;
    }

    &.without {
      background-color: rgb(113, 117, 121);
    }
  }
`;

const TcpoStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 15px;
  top: -25px;
  position: absolute;
  right: 0;

  .value {
    font-family: "IBM Plex Mono", monospace !important;
    font-size: 24px;
    color: var(--text-primary) !important;
    line-height: 1;

    span {
      font-family: "AllianceNo2-SemiBold", sans-serif;
      font-size: 12px;
      margin-left: 2px;
      color: #6b7280;
    }
  }

  .labelNr {
    color: #6b7280;
    font-size: 12px;
    margin-top: 5px;
  }
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-primary);
`;

const CHART_HEIGHT = 260;
const CHART_MARGIN = { top: 0, right: 15, bottom: 50, left: 85 };

const getTCPOData = ({ hours, mode, bqnId }) => {
  return ifCl.run(
    atBqnTarget(
      `show pkteng speed domain all hours ${hours || 24} max ${MAX_SERVICES_TO_FETCH}`,
      mode,
      bqnId,
    ),
  );
};

function TCPOComparison({ period, tcpo }) {
  const [tcpoNumber, tcpoPercentage] = tcpo.split(" ");

  // The chart has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId }) =>
      getTCPOData({
        hours: PERIOD_DASHBOARD_TO_HOURS[period],
        mode,
        bqnId,
      }).then((response) => parseResult(response).download),
    onError: "Error fetching TCPO Comparison data:",
    deps: [period],
    interval: LOAD_INTERVAL,
  });

  const dataChart = useMemo(() => {
    if (!data) return null;
    const { services, average } = data;
    if (!services || services.length === 0) return null;

    return [
      { name: "Network Avg.", ...average },
      { name: "" },
      ...services.slice(0, MAX_SERVICES_TO_SHOW),
    ].map((item) => {
      return {
        name: item.name,
        value2: item.opt,
        value1: item.track,
        accel: item.accel,
      };
    });
  }, [data]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!dataChart) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Subtitle>
          TCP downloads
          <br /> with <div className="with tcpo"></div> and without{" "}
          <div className="without tcpo"></div>TCPO.
        </Subtitle>
        <TcpoStat>
          <div className="value">
            {tcpoNumber}
            <span>{tcpoPercentage}</span>
          </div>
          <div className="labelNr">TCPO Optimized</div>
        </TcpoStat>
      </HeaderRow>
      <DumbBellChart
        data={dataChart}
        height={CHART_HEIGHT}
        margin={CHART_MARGIN}
        xAxisLabel="Flow speed (Mbps)"
      />
    </Wrapper>
  );
}

export default React.memo(TCPOComparison);
